import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Debounced autosave for a value that is saved whole (the annotation mutation
 * replaces the video's entire list).
 *
 * - Every user change bumps `version`; a save starts `delayMs` after the last one.
 * - Saves never overlap: a change made during a save is saved right after it,
 *   so an older snapshot can never land after a newer one.
 * - `needsConfirmation` holds a save back until `saveNow({ confirmed: true })`
 *   (an empty list would delete every note on the server).
 * - Disabled (e.g. the initial load failed) means nothing is ever sent.
 * - On unmount, pending changes are flushed unless `discard()` was called.
 */

export type AutosaveStatus =
  | 'idle'
  | 'pending'
  | 'unsaved'
  | 'saving'
  | 'saved'
  | 'error'
  | 'needs-confirmation'
  | 'disabled';

interface UseAutosaveOptions<T> {
  value: T;
  version: number;
  enabled: boolean;
  save: (value: T) => Promise<void>;
  delayMs?: number;
  needsConfirmation?: (value: T) => boolean;
}

export interface Autosave {
  status: AutosaveStatus;
  error: string | null;
  lastSavedAt: Date | null;
  hasUnsavedChanges: boolean;
  /** Save immediately. Resolves true once everything is saved. */
  saveNow: (options?: { confirmed?: boolean }) => Promise<boolean>;
  /** Close the confirmation without saving; the changes stay unsaved. */
  dismissConfirmation: () => void;
  /** Leaving without saving: skip the flush on unmount. */
  discard: () => void;
}

export function useAutosave<T>({
  value,
  version,
  enabled,
  save,
  delayMs = 1500,
  needsConfirmation,
}: UseAutosaveOptions<T>): Autosave {
  const [status, setStatus] = useState<AutosaveStatus>(enabled ? 'idle' : 'disabled');
  const [error, setError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [savedVersion, setSavedVersion] = useState(version);

  const valueRef = useRef(value);
  valueRef.current = value;
  const versionRef = useRef(version);
  versionRef.current = version;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const saveRef = useRef(save);
  saveRef.current = save;
  const confirmRef = useRef(needsConfirmation);
  confirmRef.current = needsConfirmation;

  const savedVersionRef = useRef(version);
  const timerRef = useRef<number | null>(null);
  const inFlightRef = useRef<Promise<boolean> | null>(null);
  const discardedRef = useRef(false);
  const mountedRef = useRef(true);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const markSaved = (savedAt: number) => {
    savedVersionRef.current = savedAt;
    if (mountedRef.current) setSavedVersion(savedAt);
  };

  const run = useCallback(async (confirmed: boolean): Promise<boolean> => {
    clearTimer();
    if (!enabledRef.current) return false;

    // Wait out a save in progress, then save whatever is newest.
    if (inFlightRef.current) {
      await inFlightRef.current.catch(() => false);
      return run(confirmed);
    }

    const target = versionRef.current;
    if (target === savedVersionRef.current) {
      if (mountedRef.current) setStatus('saved');
      return true;
    }

    const snapshot = valueRef.current;
    if (!confirmed && confirmRef.current?.(snapshot)) {
      if (mountedRef.current) setStatus('needs-confirmation');
      return false;
    }

    if (mountedRef.current) {
      setStatus('saving');
      setError(null);
    }

    const attempt = (async () => {
      try {
        await saveRef.current(snapshot);
        markSaved(target);
        if (mountedRef.current) setLastSavedAt(new Date());
        return true;
      } catch (err) {
        if (mountedRef.current) {
          setStatus('error');
          setError(err instanceof Error && err.message ? err.message : 'Your changes could not be saved.');
        }
        return false;
      }
    })();
    inFlightRef.current = attempt;
    const ok = await attempt;
    inFlightRef.current = null;
    if (!ok) return false;

    if (versionRef.current !== target) {
      // More changes arrived while saving: save them too.
      return run(false);
    }
    if (mountedRef.current) setStatus('saved');
    return true;
  }, []);

  // Schedule a save after each change.
  useEffect(() => {
    if (!enabled) {
      clearTimer();
      setStatus('disabled');
      return;
    }
    if (version === savedVersionRef.current) {
      setStatus((current) => (current === 'disabled' ? 'idle' : current));
      return;
    }
    if (inFlightRef.current) {
      // The in-flight save re-runs for this version when it finishes.
      return;
    }
    setStatus('pending');
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void run(false);
    }, delayMs);
  }, [version, enabled, delayMs, run]);

  // Flush on unmount (in-app navigation that is not the back button).
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimer();
      if (
        !discardedRef.current &&
        enabledRef.current &&
        !inFlightRef.current &&
        versionRef.current !== savedVersionRef.current &&
        !confirmRef.current?.(valueRef.current)
      ) {
        void saveRef.current(valueRef.current).catch(() => undefined);
      }
    };
  }, []);

  const saveNow = useCallback((options?: { confirmed?: boolean }) => run(Boolean(options?.confirmed)), [run]);

  const dismissConfirmation = useCallback(() => {
    setStatus(versionRef.current === savedVersionRef.current ? 'saved' : 'unsaved');
  }, []);

  const discard = useCallback(() => {
    discardedRef.current = true;
    clearTimer();
  }, []);

  return {
    status,
    error,
    lastSavedAt,
    hasUnsavedChanges: version !== savedVersion,
    saveNow,
    dismissConfirmation,
    discard,
  };
}
